import classNames from 'classnames'
import { RadioGroup, TextField } from 'formik-mui'
import React from 'react'
import { Gender, newEducation, newRole, Person } from 'scenes/Dashboard/data/people/types'
import { insertItem, removeItemByIndex } from 'utils-next/arrays'
import { FastField, FormManager, FormProps } from 'utils-next/forms'
import * as Yup from 'yup'

import { Fade, FormControlLabel, FormLabel, Radio, Typography } from '@mui/material'
import { withStyles, WithStyles } from '@mui/styles'

import AddItemButton from './components/AddItemButton.component'
import EducationForm from './components/Forms/EducationForm.component'
import PositionForm, { positionValidationSchema } from './components/Forms/PositionForm.component'
import { EmailValidationStatusSelect } from './EmailValidationStatusSelect.component'
import {
  EducationFormValues,
  mapFormValuesToPerson,
  mapPersonToFormValues,
  PersonFormValues,
  PositionFormValues,
} from './PersonForm.model'
import styles from './PersonForm.style'
import PersonFormFooter from './PersonFormFooter.component'

class PersonForm extends React.PureComponent<PersonFormProps & WithStyles<typeof styles>> {
  constructor(props: PersonFormProps & WithStyles<typeof styles>) {
    super(props)

    this.renderForm = this.renderForm.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  renderForm(props: FormProps<PersonFormValues>) {
    const { handleSubmit, isSubmitting, setFieldValue, values, resetForm } = props
    const { className, onCancel, onEdit, classes, person } = this.props
    const isInNewMode = !person.id
    const isInEditMode = this.props.isInEditMode || isInNewMode

    const onNewPosition = () => {
      setFieldValue('roles', insertItem(values.roles, newRole()))
    }

    const handleDeletePosition = (position: PositionFormValues, index: number) => {
      if (position.id < 0) {
        setFieldValue('roles', removeItemByIndex(values.roles, index))
      } else {
        setFieldValue(`roles[${index}].toDelete`, true)
      }
    }

    const onNewEducation = () => {
      setFieldValue('educations', insertItem(values.educations, newEducation()))
    }

    const handleDeleteEducation = (education: EducationFormValues, index: number) => {
      if (education.id < 0) {
        setFieldValue('educations', removeItemByIndex(values.educations, index))
      } else {
        setFieldValue(`educations[${index}].toDelete`, true)
      }
    }

    const handleCancel = () => {
      resetForm()
      onCancel()
    }

    return (
      <Fade in={true}>
        <div className={classNames(className, classes.formContainer)}>
          <form>
            <FastField
              component={TextField}
              label="First name"
              name="firstName"
              disabled={isSubmitting}
              className={classes.field}
              required
              fullWidth
              autoFocus
            />
            <FastField
              component={TextField}
              label="Last name"
              name="lastName"
              disabled={isSubmitting}
              className={classes.field}
              required
              fullWidth
            />
            <div className={classes.gender}>
              <FormLabel className={classes.genderLabel} component="legend">
                Gender
              </FormLabel>
              <FastField name="gender" className={classes.genderInput} label="Gender" component={RadioGroup}>
                <FormControlLabel
                  value={Gender.male}
                  control={<Radio disabled={isSubmitting} />}
                  label="Male"
                  disabled={isSubmitting}
                />
                <FormControlLabel
                  value={Gender.female}
                  control={<Radio disabled={isSubmitting} />}
                  label="Female"
                  disabled={isSubmitting}
                />
                <FormControlLabel value={Gender.other} control={<Radio disabled={isSubmitting} />} label="Other" />
              </FastField>
            </div>
            <FastField
              component={TextField}
              label="Linkedin URL"
              name="linkedinUrl"
              disabled={isSubmitting}
              className={classes.extraField}
              fullWidth
            />
            <FastField
              component={TextField}
              label="Email"
              name="email"
              disabled={isSubmitting}
              className={classes.extraField}
              fullWidth
            />
            <FastField
              component={EmailValidationStatusSelect}
              className={classes.field}
              label="Email validation status"
              name="emailValidationStatus"
              required
            />
            <FastField
              component={TextField}
              label="Born on"
              name="bornOn"
              type="date"
              disabled={isSubmitting}
              className={classes.extraField}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
            <FastField
              component={TextField}
              label="Died on"
              name="diedOn"
              type="date"
              disabled={isSubmitting}
              className={classes.extraField}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />

            <FastField
              component={TextField}
              label="Blurb"
              name="blurb"
              disabled={isSubmitting}
              className={classes.field}
              multiline
              fullWidth
            />

            <FastField
              component={TextField}
              label="Description"
              name="description"
              disabled={isSubmitting}
              className={classes.field}
              multiline
              fullWidth
            />

            <div className={classes.formSection}>
              <Typography variant="subtitle1">Positions</Typography>
              <div className={classes.sectionList}>
                {values.roles &&
                  values.roles.map((position, index) =>
                    !position.toDelete ? (
                      <PositionForm
                        key={`position-item-${position.id}-${index}`}
                        className={classes.sectionForm}
                        name={`roles[${index}]`}
                        position={position}
                        index={index}
                        onDelete={handleDeletePosition}
                      />
                    ) : null
                  )}
              </div>
              <AddItemButton onClick={onNewPosition} label="Add position">
                Add position
              </AddItemButton>
            </div>
            <div className={classes.formSection}>
              <Typography variant="subtitle1">Education</Typography>
              <div className={classes.sectionList}>
                {values.educations &&
                  values.educations.map((education, index) =>
                    !education.toDelete ? (
                      <EducationForm
                        key={`education-item-${education.id}-${index}`}
                        className={classes.sectionForm}
                        name={`educations[${index}]`}
                        education={education}
                        index={index}
                        onDelete={handleDeleteEducation}
                      />
                    ) : null
                  )}
              </div>
              <AddItemButton onClick={onNewEducation} label="Add education">
                Add education
              </AddItemButton>
            </div>
          </form>
          <div className={classes.footerContainer}>
            <hr />
            <PersonFormFooter
              className={classes.footerSelector}
              isSubmitting={isSubmitting}
              isInEditMode={isInEditMode}
              isInNewMode={isInNewMode}
              onSave={handleSubmit}
              onBack={handleCancel}
              onEdit={onEdit}
              onDelete={() => null}
            />
          </div>
        </div>
      </Fade>
    )
  }

  handleSubmit(values: PersonFormValues) {
    const { onSubmit } = this.props
    const person = mapFormValuesToPerson(values)
    onSubmit(person)
  }

  render() {
    const { person } = this.props
    const personFormValues = mapPersonToFormValues(person)
    return (
      <FormManager
        initialValues={personFormValues}
        onSubmit={this.handleSubmit}
        render={this.renderForm}
        validationSchema={personValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
      />
    )
  }
}

export const personValidationSchema = Yup.object().shape({
  firstName: Yup.string().required('Variable firstName is required'),
  lastName: Yup.string().required('Variable lastName is required'),
  email: Yup.string().email(),
  roles: Yup.array().of(positionValidationSchema),
  // education: Yup.array().of(educationValidationSchema),
})

export type PersonFormProps = {
  className?: string
  person: Person
  isInEditMode: boolean
  onSubmit: (person: Person) => void
  onCancel: () => void
  onEdit: () => void
}

export default withStyles(styles)(PersonForm)
